import * as React from 'react';
import { useIntl } from 'react-intl';
import Modal, { ModalAction } from 'components/@client/Modal';
import { Message } from 'i18n';
import { useFetch, useNotifications } from 'hooks';
import { METHODS } from 'api/client';
import { Project } from 'types/properties';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  property: Project;
  onRefresh?: VoidFunction;
}

const DeleteProjectModal: React.FC<Props> = ({
  open,
  onClose,
  property,
  onRefresh,
}) => {
  const intl = useIntl();
  const { showNotification } = useNotifications();
  const onSuccess = React.useCallback(
    _ => {
      showNotification({
        key: 'clients/deletePropertyFulfilled',
        message: 'Property has been deleted successfully.',
        severity: 'success',
      });
      onClose();
      onRefresh?.();
    },
    [showNotification, onClose, onRefresh]
  );

  const onFailure = React.useCallback(
    (message: string) => {
      showNotification({
        key: 'clients/deletePropertyRejected',
        message,
        severity: 'error',
      });
      onClose();
    },
    [showNotification, onClose]
  );

  const { callFetch, isLoading } = useFetch<{}>({
    initialUrl: '/api/projects/',
    skipOnStart: true,

    onSuccess,
    onFailure,
  });

  const onDeleteProperty = React.useCallback(() => {
    callFetch({ url: `/api/projects/${property.id}/`, method: METHODS.DELETE });
  }, [callFetch, property]);

  const actions: ModalAction[] = [
    {
      label: intl.formatMessage({ id: Message.NO }),
      onClick: () => onClose(),
      variant: 'secondary',
    },
    {
      label: intl.formatMessage({ id: Message.YES }),
      onClick: onDeleteProperty,
      variant: 'primary',
      isLoading,
    },
  ];

  return (
    <Modal
      open={open || false}
      onClose={onClose}
      title={intl.formatMessage({ id: Message.DELETE_PROJECT_TEXT })}
      actions={actions}
    >
      {null}
    </Modal>
  );
};

export default DeleteProjectModal;
